<template>
  <v-card flat class="pa-4" style="overflow: visible !important;z-index:1!important;">
      <v-row v-if="datesDiffer" class="mb-2">
        <v-col sm="12">
          <v-alert type="info" density="compact" variant="tonal" icon="mdi-information">
            {{ $t('dateAdministrativeDiffersWarning') }}
          </v-alert>
        </v-col>
      </v-row>
      <h3 class="text-subtitle-1 font-weight-bold mb-1">{{ $t('execution') }}</h3>
      <v-row >
        <v-col sm="6" >
          <!-- Date: Date start -->
          <Datepicker
            id="date_start"
            :placeholder="$t('executionDateStart')"
            :model-value="dateStart"
            :name="'date_start'"
            :maxDate="dateEnd ? dateEnd: ''"
            @update-dates-and-times="update"
            @keyup.tab="focusNextInput('date_start')"
          />
        </v-col>
        <!-- Time: Time start -->
        <v-col sm="6" >
          <Timepicker
            id="time_start"
            :placeholder="$t('executionTimeStart')"
            :model-value="timeStart"
            :name="'time_start'"
            @update-dates-and-times="update"
            @keyup.tab="focusNextInput('time_start')"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="12" class="py-0">
          <!-- Checkbox: datetime start locked -->
          <v-checkbox
            v-model="datetimeStartLocked"
            :label="$t('datetimeStartLocked')"
            hide-details
            @change="update({ name: 'datetime_start_locked', value: datetimeStartLocked })"
          ></v-checkbox>
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="6" >
          <!-- Date: date end -->
          <Datepicker
            v-if="!workOrder.is_for_equipment_movement"
            id="date_end"
            :placeholder="$t('executionDateEnd')"
            :model-value="dateEnd"
            :name="'date_end'"
            @update-dates-and-times="update"
            :minDate="dateStart ? dateStart: ''"
            @keyup.tab="focusNextInput('date_end')"
          />
        </v-col>
        <!-- Time: Time end -->
        <v-col sm="6" >
          <Timepicker
            v-if="!workOrder.is_for_equipment_movement"
            id="time_end"
            :placeholder="$t('executionTimeEnd')"
            :model-value="timeEnd"
            :name="'time_end'"
            @update-dates-and-times="update"
          />
        </v-col>
      </v-row>
      <h3 class="text-subtitle-1 font-weight-bold mt-4 mb-1">{{ $t('dateAdministrative') }}</h3>
      <v-row>
        <v-col sm="6" >
          <!-- Date: administrative date -->
          <Datepicker
            id="date_administrative"
            :placeholder="$t('dateAdministrative')"
            :model-value="dateAdministrative"
            :name="'date_administrative'"
            @update-dates-and-times="update"
            @keyup.tab="focusNextInput('date_administrative')"
          />
          <div class="text-caption text-medium-emphasis mt-1">
            {{ $t('dateAdministrativeExplanation') }}
            {{ $t('dateAdministrativeHint') }}
          </div>
        </v-col>
      </v-row>
  </v-card>
</template>

<script setup lang="ts">
import Datepicker from '@/components/UI/Datepicker.vue';
import Timepicker from '@/components/UI/Timepicker.vue';
import emitArray from '@/utils/emit';
import '@vuepic/vue-datepicker/dist/main.css'
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import { watch } from 'vue';
import { computed, onBeforeMount, defineComponent, ref } from 'vue';
import { focusNextInput } from '@/utils/functions';

const emit = defineEmits(emitArray.value)
defineComponent({
  components: {
    Datepicker,
    Timepicker
  }
})

const props = defineProps({
  workOrder: {
    type: Object,
    required: true
  },
  originalWorkOrder: {
    type: Object as any,
    reqired: false
  }
})

const dateStart = ref(null as any)
const dateEnd = ref(null as any)
const timeStart = ref(null as any)
const timeEnd = ref(null as any)
const datetimeStartLocked = ref(false)
const dateAdministrative = ref(null as any)
const originalWorkOrder = ref(null as any)

onBeforeMount(() => {
  originalWorkOrder.value = cloneDeep(props.workOrder)
  initializeInputs()
})

watch(props.workOrder, () => {
  dateStart.value = props.workOrder.date_start ? moment(props.workOrder.date_start) : null
  timeStart.value = props.workOrder.time_start ? formatTime(props.workOrder.time_start) : null
  dateEnd.value = props.workOrder.date_end ? moment(props.workOrder.date_end) : null
  timeEnd.value = props.workOrder.time_end ? formatTime(props.workOrder.time_end) : null
  datetimeStartLocked.value = !!props.workOrder.datetime_start_locked
  dateAdministrative.value = props.workOrder.date_administrative ? moment(props.workOrder.date_administrative) : null
}, {  deep: true })


const initializeInputs = () => {
  dateStart.value = originalWorkOrder.value.date_start ? moment(originalWorkOrder.value.date_start) : null
  timeStart.value = originalWorkOrder.value.time_start ? formatTime(originalWorkOrder.value.time_start) : null
  dateEnd.value = originalWorkOrder.value.date_end ? moment(originalWorkOrder.value.date_end) : null
  timeEnd.value = originalWorkOrder.value.time_end ? formatTime(originalWorkOrder.value.time_end) : null
  datetimeStartLocked.value = !!originalWorkOrder.value.datetime_start_locked
  dateAdministrative.value = originalWorkOrder.value.date_administrative ? moment(originalWorkOrder.value.date_administrative) : null
}

const formatTime = (time: any) => {
  if (!time) {
    return ;
  }

  const hour = parseInt(time.split(':')[0]);
  const minute = parseInt(time.split(':')[1]);
  const seconds = parseInt(time.split(':')[2]) || 0;

  return {
    hours: hour,
    minutes: minute,
    seconds: seconds
  }
}

const update = (data: any) => {
  emit('change', data)
}

const datesDiffer = computed(() => {
  if (!dateStart.value || !dateAdministrative.value) {
    return false
  }

  return moment(dateStart.value).format('YYYY-MM-DD') !== moment(dateAdministrative.value).format('YYYY-MM-DD')
})

defineExpose({
  initializeInputs
});
</script>

